import {
    Box,
    Button,
    Drawer,
    Grid,
    IconButton,
    Stack,
    Divider,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Link,
    Chip,
    Stepper,
    Step,
    StepLabel,
    StepContent,
    Tooltip,
    alpha,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import StickyNote2Icon from '@mui/icons-material/StickyNote2';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import dayjs from 'dayjs';

import _ from 'lodash';
import { Else, If, Then, When } from 'react-if';

import { AlertTypes } from '../../types';
import { hasDispatchDisposition, isMonitoringCenterCanceled } from '../../utils/alert-disposition';
import { ALERT_TYPE_LABELS } from '../../helpers/alertTypeLabels';
import { getInitiatedByDescription } from '../../helpers/initiatedByDescription';
import { ComponentPermissionGateway } from '../component-permission-gateway';
import {
    customerPermissionFlags,
} from '../../constants/systemPermissions.constants';
import { useState, useEffect } from 'react';
import { ResolveAlertModal } from '../resolve-alert-modal';
import { AlertPathMapComponent } from './alert-path-map-component';
import { getCachedAddress, setCachedAddress } from './reverse-geocode-cache';
import { useGenericQuery } from '../../services/react-query/generic.query';
import Loading from '../loading';
import { getTranscriptRecordFile } from '../../http';
import { useGenericMutation } from '../../services/react-query/generic.mutation';
import { LoadingButton } from '@mui/lab';
import { toast } from 'react-toastify';

type OptionalFunctionProp = () => void | undefined;

interface Props {
    onClose(): void;
    visible: boolean;
    type?: string,
    alert: AlertTypes.Model;
    zoomOut?: OptionalFunctionProp;
    onOpenAppointment?: (appointmentId: string) => void;
    onOpenAlert?: (alertId: string) => void;
}

type ChipColor = 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';

// Types that don't involve a real call/dispatch — they shouldn't render the
// Call Recording section (which would just show "no transcript / no audio")
// and benefit from a one-line descriptor so reviewers know what they're
// looking at without scrolling.
const NO_CALL_TYPES = new Set(['CheckOnMe', 'Shift', 'FakeCall', 'RiskReview']);

const TYPE_DESCRIPTIONS: Record<string, string> = {
    FakeCall: 'User triggered a fake incoming call from the app — a simulated call was placed to their phone.',
    RiskReview: 'Risk assessment submitted by the user as part of a visit/check-in.',
};

const FieldRow: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
    <Grid item md={4} sm={12} xs={12}>
        <Typography fontSize={13} fontWeight={500} color="text.secondary">
            {label}
        </Typography>
        <Typography fontSize={14} fontWeight={400} mt={0.5}>
            {children}
        </Typography>
    </Grid>
);

const SectionHeader: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Grid item md={12} sm={12} xs={12}>
        <Typography variant="subtitle2" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.4, color: 'text.secondary' }}>
            {children}
        </Typography>
    </Grid>
);

export const AlertDetailsDrawer: React.FC<Props> = ({
    onClose,
    visible,
    alert,
    type,
    zoomOut,
    onOpenAppointment,
    onOpenAlert,
}) => {
    const [escalated, setEscalated] = useState(alert?.escalated);
    const [audioUrl, setAudioUrl] = useState<string>('');
    const [audioLoading, setAudioLoading] = useState(false);
    const {
        data: callTranscript,
        isLoading,
        isFetched,
    } = useGenericQuery<AlertTypes.CallTranscript>(
        'getAlertCallTranscript',
        `alerts/${alert ? alert?.id : ''}/getCallTranscript`,
        undefined,
    );
    const [resolveModal, setResolveModal] = useState(false);
    const findFirst = (e: unknown) => e !== undefined;
    const location = alert?.locations?.find(findFirst);

    const [showAllLocations, setShowAllLocations] = useState(false);

    const lat = Number(location?.latitude);
    const lng = Number(location?.longitude);
    const hasCoords =
        !!location?.latitude &&
        !!location?.longitude &&
        !Number.isNaN(lat) &&
        !Number.isNaN(lng) &&
        (lat !== 0 || lng !== 0);

    const [resolvedAddress, setResolvedAddress] = useState<string | null>(null);
    const [resolveError, setResolveError] = useState<string | null>(null);

    useEffect(() => {
        setResolvedAddress(null);
        setResolveError(null);
    }, [alert?.id]);

    const reverseGeocodeKey = hasCoords
        ? `pom:reverse-geocode:${lat.toFixed(5)},${lng.toFixed(5)}`
        : null;

    useEffect(() => {
        if (!visible || !hasCoords || !reverseGeocodeKey) return;

        let cancelled = false;
        const run = async () => {
            const cached = getCachedAddress(reverseGeocodeKey);
            if (cached) {
                if (!cancelled) setResolvedAddress(cached);
                return;
            }

            if (!window.google?.maps?.Geocoder) {
                if (!cancelled) setResolveError('Maps not loaded yet.');
                return;
            }

            try {
                const geocoder = new window.google.maps.Geocoder();
                const result = await geocoder.geocode({ location: { lat, lng } });
                const address = result.results?.[0]?.formatted_address ?? null;
                if (cancelled) return;
                if (!address) {
                    setResolveError('No address found for these coordinates.');
                    return;
                }
                setResolvedAddress(address);
                setCachedAddress(reverseGeocodeKey, address);
            } catch (err) {
                console.error('[AlertDetailsDrawer] Reverse-geocode failed:', err);
                if (!cancelled) setResolveError('Could not look up address.');
            }
        };
        run();

        return () => {
            cancelled = true;
        };
        // Why: only re-run on open, alert change, or coord change. The
        // in-memory cache makes redundant runs cheap, so we don't need
        // `resolvedAddress` in the deps to prevent re-firing.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible, alert?.id, reverseGeocodeKey]);

    // Fetch audio URL when callTranscript is available
    useEffect(() => {
        const fetchAudioUrl = async () => {
            if (callTranscript?.id && callTranscript?.Media?.MediaFileUri) {
                setAudioLoading(true);
                try {
                    const url = await getTranscriptRecordFile(callTranscript.id);
                    setAudioUrl(url);
                } catch (error) {
                    console.error('Failed to fetch audio file:', error);
                    setAudioUrl('');
                } finally {
                    setAudioLoading(false);
                }
            }
        };

        fetchAudioUrl();
    }, [callTranscript?.id, callTranscript?.Media?.MediaFileUri]);

    const { mutateAsync: escalateAsync, isLoading: escalateLoading } = useGenericMutation<{
        phoneNumber: string,
        locations: AlertTypes.AlertLocation[],
    }, boolean>('escalateCheckOnMe', `alerts/escalate/${alert?.id}`, 'post');

    const isCheckOnMeOrShift = type === 'CheckOnMe' || type === 'Shift';
    const hasCallRecordingContent = !!callTranscript?.TranscriptionText || !!callTranscript?.Media?.MediaFileUri;
    // Hide the whole section once the transcript query resolves with nothing —
    // an empty "no transcript / no audio" block isn't useful. While the query
    // is still in flight (!isFetched) we keep it visible so the loading state
    // can render. Partial content (transcript only or audio only) still shows.
    const showCallRecording = !!type && !NO_CALL_TYPES.has(type) && (!isFetched || hasCallRecordingContent);
    const typeDescription = type ? TYPE_DESCRIPTIONS[type] : undefined;
    const isResolved = !!alert?.resolvedAt;
    const lifecycleStatus = type === 'Shift' ? alert?.shiftStatus : alert?.checkOnMeStatus;
    const wasExpired = lifecycleStatus === 'expired';
    const wasEscalated = !!escalated || lifecycleStatus === 'escalated' || lifecycleStatus === 'escalating';

    const typeLabel = (type && ALERT_TYPE_LABELS[type]) || type || 'Alert';

    // Status chip — surfaces the alert's actual outcome at a glance.
    const statusChip: { label: string; color: ChipColor } = (() => {
        if (isResolved) return { label: 'Resolved', color: 'success' };
        if (wasEscalated) return { label: 'Escalated', color: 'error' };
        if (wasExpired) return { label: 'Expired', color: 'error' };
        if (alert?.status === 'active') return { label: 'Active', color: 'warning' };
        return { label: alert?.status || 'Unknown', color: 'default' };
    })();

    const typeChipColor: ChipColor =
        type === 'SOS' ? 'error' :
        type === 'CheckOnMe' || type === 'Shift' ? 'info' :
        type === 'FakeCall' || type === 'RiskReview' ? 'info' :
        'default';

    const timerDisplay = type === 'Shift'
        ? `${alert?.options?.shiftMinutes ? (alert.options.shiftMinutes / 60).toFixed(1).replace(/\.0$/, '') : 'N/A'} hrs`
        : `${alert?.checkOnMeActiveMinutes ?? 'N/A'} min`;

    // Lifecycle steps for Check on Me / Shift. Shows every phase the org has
    // configured so reviewers can see where the alert is and what comes next.
    // Past steps render with timestamps; future steps render as "pending".
    type LifecycleStep = {
        key: string;
        label: string;
        caption: React.ReactNode;
        state: 'done' | 'current' | 'pending';
    };
    const lifecycleSteps: LifecycleStep[] = (() => {
        if (!isCheckOnMeOrShift || !alert?.initiatedAt) return [];

        const started = dayjs(alert.initiatedAt);
        const minutes = type === 'Shift' ? alert?.options?.shiftMinutes : alert?.checkOnMeActiveMinutes;
        const scheduledExpiry = minutes != null ? started.add(minutes, 'minute') : null;
        const status = lifecycleStatus || 'started';

        const steps: LifecycleStep[] = [];

        steps.push({
            key: 'started',
            label: type === 'Shift' ? 'Shift started' : 'Check on Me started',
            caption: started.format('lll'),
            state: 'done',
        });

        // Each "add time" extension the user made, recorded by node-api. Surfaced
        // chronologically right after the start so the stepper reads as a timeline
        // of the active window (started → extensions → reminder → expiry/escalation).
        // Only Check on Me persists these; for Shifts the array is absent.
        [...(alert?.extensionHistory ?? [])]
            .sort((a, b) => dayjs(a.createdAt).valueOf() - dayjs(b.createdAt).valueOf())
            .forEach((ext, i) => {
                steps.push({
                    key: `extension-${i}`,
                    label: `Time extended · +${ext.minutesAdded} min`,
                    caption: `${dayjs(ext.createdAt).format('lll')} · now ${ext.totalMinutes} min total`,
                    state: 'done',
                });
            });

        // Check on Me has an interim "reminded" phase; Shifts do not.
        if (type !== 'Shift') {
            const reminded = ['reminded', 'expired', 'escalating', 'escalated'].includes(status);
            steps.push({
                key: 'reminded',
                label: 'Reminder sent',
                caption: reminded ? 'Reminder delivered to user' : 'Pending — user will be reminded before expiry',
                state: reminded ? 'done' : 'pending',
            });
        }

        // An escalated Check on Me can ALSO carry a resolvedAt (legacy race docs)
        // or a checkedOutAt (checkout that landed after escalation). The escalation
        // must stay visible — never collapse it into a plain "Checked out" step.
        const escalatedLifecycle = ['escalating', 'escalated'].includes(status) || !!alert?.escalatedAt;

        if (isResolved && alert?.resolvedAt && !escalatedLifecycle) {
            steps.push({
                key: 'resolved',
                label: type === 'Shift' ? 'Shift ended' : 'Checked out',
                caption: `${dayjs(alert.resolvedAt).format('lll')}${alert.resolvedBy ? ` · by ${alert.resolvedBy}` : ''}`,
                state: 'done',
            });
            return steps;
        }

        const expired = ['expired', 'escalating', 'escalated'].includes(status);
        steps.push({
            key: 'expired',
            label: type === 'Shift' ? 'Shift expired' : 'Check on Me expired',
            caption: expired
                ? (alert?.expiredAt ? dayjs(alert.expiredAt).format('lll') : 'Expired')
                : (scheduledExpiry ? `Scheduled · ${scheduledExpiry.format('lll')}` : 'Scheduled'),
            state: expired ? 'done' : 'pending',
        });

        const escalatedSosId = alert?.escalatedSosAlertId;
        const escalatedCaption: React.ReactNode = status === 'escalated' ? (
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Typography fontSize={13} color="text.secondary">
                    {alert?.escalatedAt ? dayjs(alert.escalatedAt).format('lll') : 'Escalated'}
                </Typography>
                {escalatedSosId && (
                    onOpenAlert ? (
                        <Link
                            component="button"
                            variant="body2"
                            underline="hover"
                            sx={{ fontSize: 13, fontWeight: 500 }}
                            onClick={(e: React.MouseEvent) => {
                                e.stopPropagation();
                                onOpenAlert(escalatedSosId);
                            }}
                        >
                            View SOS alert →
                        </Link>
                    ) : (
                        <Typography fontSize={13} color="text.secondary">
                            · SOS alert opened
                        </Typography>
                    )
                )}
            </Stack>
        ) : (
            'Will trigger automatically if not resolved (per org settings)'
        );

        steps.push({
            key: 'escalated',
            label: 'Escalated to SOS',
            caption: status === 'escalating' ? 'Escalation in progress' : escalatedCaption,
            state: status === 'escalated' ? 'done' : (status === 'escalating' ? 'current' : 'pending'),
        });

        // Checkout that landed after (or raced) the escalation: shown as its own
        // step so both facts stay visible. checkedOutAt is the annotate field the
        // backend writes for race losers; resolvedAt covers legacy race docs.
        const checkedOutAfterEscalation = alert?.checkedOutAt || (isResolved ? alert?.resolvedAt : null);
        if (escalatedLifecycle && checkedOutAfterEscalation) {
            steps.push({
                key: 'resolved',
                label: type === 'Shift' ? 'Shift ended · after escalation' : 'Checked out · after escalation',
                caption: dayjs(checkedOutAfterEscalation).format('lll'),
                state: 'done',
            });
        }

        return steps;
    })();

    // SOS alerts spawned from a Check on Me are tagged on the backend with
    // initiatedBy='CheckOnMe' and a checkOnMeId pointer back to the original.
    // Surfacing this in the timeline makes the origin obvious at a glance.
    const isSosFromCheckOnMe = type === 'SOS' && alert?.initiatedBy === 'CheckOnMe';

    const showAlertStatusTimeline = (alert?.dispatchTrail?.length ?? 0) > 0 || isSosFromCheckOnMe;

    // Risk Review survey content — surfaces the scores and comment the user
    // submitted as part of the visit. Only rendered for RiskReview alerts.
    // Field Safety Reports go through the same alertType but never carry the
    // numeric scores, so we suppress the score rows for them entirely.
    const isFromFieldSafetyReport = /field.?safety/i.test(alert?.initiatedBy || '');
    const showRiskReviewScores = type === 'RiskReview' && !isFromFieldSafetyReport && (
        alert?.riskReviewSurveyHomeScore !== undefined ||
        alert?.riskReviewSurveyNeighborhoodScore !== undefined
    );
    const showRiskReview = type === 'RiskReview' && (showRiskReviewScores || !!alert?.riskReviewSurveyComment);

    // Middle column carries the timeline (SOS), call recording (SOS / Appointment),
    // lifecycle stepper (Check on Me / Shift), or risk-review scores (RiskReview).
    // For Fake Call — none of those — collapsing the middle column lets the
    // remaining content breathe instead of leaving a giant empty gap.
    const showMiddleColumn = showAlertStatusTimeline || showCallRecording || isCheckOnMeOrShift || showRiskReview;
    const sideColumnSize = showMiddleColumn ? 4 : 6;

    // Dispatch trail entries from the monitoring service stream in over time.
    // Sort oldest → newest so the stepper reads top-to-bottom in chronological
    // order, with the latest event highlighted as the active step.
    const sortedDispatchTrail = [...(alert?.dispatchTrail ?? [])].sort((a, b) =>
        dayjs(a.createdAt).valueOf() - dayjs(b.createdAt).valueOf(),
    );

    const handleEscalate = async () => {
        await escalateAsync({
            phoneNumber: alert?.user?.phoneNumber!,
            locations: alert?.locations,
        }, {
            onError: () => {
                toast('An error occurred while escalating the alert.', {
                    autoClose: 1500,
                    type: 'error',
                });
            },
            onSuccess: () => {
                setEscalated(true);
                toast('Alert successfully escalated.', {
                    autoClose: 1500,
                    type: 'success',
                });
            },
        });
    };

    // Primary action — Resolve. Permission gating preserved from prior
    // SOS-only behavior; Check on Me / Shift Resolve stays ungated as before.
    const resolveButton = (
        <Button
            variant="contained"
            color="primary"
            size="small"
            disabled={alert?.status !== 'active'}
            onClick={() => setResolveModal(true)}
        >
            Resolve alert
        </Button>
    );

    return (
        <>
            <Drawer
                anchor="bottom"
                open={visible}
                onClose={onClose}
                sx={{ zIndex: '2000' }}
            >
                <Stack
                    direction={'column'}
                    justifyContent={'space-between'}
                    style={{
                        backgroundColor: '#FFF',
                    }}
                >
                    <Box sx={{ minHeight: { xs: '500px', md: '500px' } }}>
                        {/* Header */}
                        <Stack
                            direction={'row'}
                            justifyContent={'space-between'}
                            alignItems="center"
                            mt={1}
                            style={{ padding: 10 }}
                        >
                            <Typography fontSize={24} fontWeight={500} textAlign={'left'}>
                                Alert Details
                            </Typography>
                            <IconButton onClick={onClose} sx={{ alignSelf: 'flex-start' }}>
                                <CloseIcon />
                            </IconButton>
                        </Stack>

                        <Divider />

                        {/* Status hero — type + status + primary action */}
                        <Box sx={{ px: 2, py: 2, backgroundColor: '#FAFBFC' }}>
                            <Stack
                                direction={{ xs: 'column', md: 'row' }}
                                justifyContent="space-between"
                                alignItems={{ xs: 'flex-start', md: 'center' }}
                                spacing={2}
                            >
                                <Stack spacing={0.75}>
                                    <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
                                        <Chip label={typeLabel} color={typeChipColor} size="small" />
                                        <Chip label={statusChip.label} color={statusChip.color} size="small" variant="outlined" />
                                        {alert?.isFalseAlarm === true && (
                                            <Chip label="Test / False Alarm" color="warning" size="small" />
                                        )}
                                        {hasDispatchDisposition(alert) && (
                                            <Tooltip title="Emergency services were dispatched for this alert" arrow>
                                                <Chip label="EMS Dispatched" color="error" size="small" />
                                            </Tooltip>
                                        )}
                                        {isMonitoringCenterCanceled(alert) && (
                                            <Tooltip title="The monitoring center canceled this alarm" arrow>
                                                <Chip label="Canceled by Monitoring Center" color="warning" size="small" />
                                            </Tooltip>
                                        )}
                                        {isResolved && alert?.resolvedAt && (
                                            <Typography fontSize={13} color="text.secondary">
                                                Resolved {dayjs(alert.resolvedAt).format('MMM D, YYYY h:mm A')}
                                            </Typography>
                                        )}
                                    </Stack>
                                    {typeDescription && (
                                        <Typography fontSize={12} color="text.secondary" sx={{ maxWidth: 640 }}>
                                            {typeDescription}
                                        </Typography>
                                    )}
                                </Stack>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    {!isResolved && (
                                        <Tooltip
                                            title="Mark this alert as resolved once you've confirmed the person is safe."
                                            arrow
                                            placement="bottom"
                                        >
                                            <span>
                                                {isCheckOnMeOrShift ? (
                                                    resolveButton
                                                ) : (
                                                    <ComponentPermissionGateway
                                                        role={customerPermissionFlags.TEAM_LEADER}
                                                    >
                                                        {resolveButton}
                                                    </ComponentPermissionGateway>
                                                )}
                                            </span>
                                        </Tooltip>
                                    )}
                                    {isCheckOnMeOrShift && !isResolved && (
                                        <Tooltip
                                            title={wasEscalated
                                                ? 'This alert has already been escalated to an SOS.'
                                                : 'Open a full SOS alert on this user for monitoring.'}
                                            arrow
                                            placement="bottom"
                                        >
                                            <span>
                                                <LoadingButton
                                                    variant="outlined"
                                                    color="error"
                                                    size="small"
                                                    disabled={wasEscalated || alert?.status !== 'active'}
                                                    loading={escalateLoading}
                                                    onClick={handleEscalate}
                                                >
                                                    {wasEscalated ? 'Escalated' : 'Escalate to SOS'}
                                                </LoadingButton>
                                            </span>
                                        </Tooltip>
                                    )}
                                </Stack>
                            </Stack>
                        </Box>

                        <Divider />

                        <Box style={{ padding: 16 }} mt={1}>
                            <Grid container spacing={2} sx={{ wordWrap: 'break-word' }}>
                                {/* LEFT COLUMN — Alert / Subject / Resolution */}
                                <Grid
                                    container
                                    spacing={2}
                                    p={2}
                                    md={sideColumnSize}
                                    sm={12}
                                    alignContent={'flex-start'}
                                >
                                    <SectionHeader>Alert</SectionHeader>
                                    <FieldRow label="Initiated By">{getInitiatedByDescription(alert?.initiatedBy)}</FieldRow>
                                    <FieldRow label="Initiated At">
                                        {alert?.initiatedAt ? dayjs(alert.initiatedAt).format('MM/DD/YYYY hh:mm A') : 'N/A'}
                                    </FieldRow>
                                    <FieldRow label="Transmitter Code">{alert?.transmitterCode || 'N/A'}</FieldRow>
                                    {/* Note + chosen location the user attached to a Check on Me. Surfaced
                                        as full-width accented callouts (not plain field rows) so responders
                                        see this subject-provided context at a glance. Presence-gated — only
                                        the Check on Me alert itself carries these fields. */}
                                    <When condition={!!alert?.checkOnMeNote || !!alert?.checkOnMeLocation?.locationName}>
                                        <Grid item md={12} sm={12} xs={12}>
                                            <Stack spacing={1.5}>
                                                <When condition={!!alert?.checkOnMeNote}>
                                                    <Box>
                                                        <Typography fontSize={11} fontWeight={500} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.4, mb: 0.5 }}>
                                                            Location Note
                                                        </Typography>
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                gap: 1.5,
                                                                borderLeft: 3,
                                                                borderColor: 'warning.main',
                                                                backgroundColor: (theme) => alpha(theme.palette.warning.main, 0.12),
                                                                borderRadius: '0 4px 4px 0',
                                                                px: 2,
                                                                py: 1.5,
                                                            }}
                                                        >
                                                            <StickyNote2Icon sx={{ fontSize: 20, color: 'warning.main', mt: '2px', flexShrink: 0 }} />
                                                            <Typography fontSize={15} sx={{ whiteSpace: 'pre-wrap' }}>
                                                                {alert?.checkOnMeNote}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                </When>
                                                <When condition={!!alert?.checkOnMeLocation?.locationName}>
                                                    <Box>
                                                        <Typography fontSize={11} fontWeight={500} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.4, mb: 0.5 }}>
                                                            Selected Location
                                                        </Typography>
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                gap: 1.5,
                                                                alignItems: 'center',
                                                                borderLeft: 3,
                                                                borderColor: 'primary.main',
                                                                backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.06),
                                                                borderRadius: '0 4px 4px 0',
                                                                px: 2,
                                                                py: 1.5,
                                                            }}
                                                        >
                                                            <LocationOnIcon sx={{ fontSize: 20, color: 'primary.main', flexShrink: 0 }} />
                                                            <Typography fontSize={15}>
                                                                {alert?.checkOnMeLocation?.locationName}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                </When>
                                            </Stack>
                                        </Grid>
                                    </When>
                                    <When condition={alert?.appointmentId || (alert?.appointment && alert?.appointment.length > 0)}>
                                        <Grid item md={4} sm={12} xs={12}>
                                            <Typography fontSize={13} fontWeight={500} color="text.secondary">
                                                Appointment
                                            </Typography>
                                            {alert?.appointmentId && onOpenAppointment ? (
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 0.5,
                                                        mt: 0.5,
                                                        cursor: 'pointer',
                                                        '&:hover': { opacity: 0.7 },
                                                    }}
                                                    onClick={() => onOpenAppointment(alert.appointmentId!)}
                                                >
                                                    <CalendarTodayIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                                                    <Link
                                                        component="button"
                                                        variant="body2"
                                                        underline="hover"
                                                        sx={{
                                                            fontSize: 14,
                                                            fontWeight: 400,
                                                            color: 'primary.main',
                                                            textAlign: 'left',
                                                        }}
                                                        onClick={(e: React.MouseEvent) => {
                                                            e.stopPropagation();
                                                            onOpenAppointment(alert.appointmentId!);
                                                        }}
                                                    >
                                                        {alert?.appointmentTitle || alert?.appointment?.trim() || 'View Appointment'}
                                                    </Link>
                                                </Box>
                                            ) : (
                                                <Typography fontSize={14} fontWeight={400} mt={0.5}>
                                                    {alert?.appointmentTitle || alert?.appointment?.trim() || 'N/A'}
                                                </Typography>
                                            )}
                                        </Grid>
                                    </When>

                                    <Grid item md={12} mt={1} sm={12} xs={12}>
                                        <Divider />
                                    </Grid>

                                    <SectionHeader>Subject</SectionHeader>
                                    <FieldRow label="Full Name">
                                        {[alert?.user?.firstName, alert?.user?.lastName].filter(Boolean).join(' ') || 'N/A'}
                                        {alert?.user?.isDeleted && (
                                            <Tooltip title="This user has been deleted — details shown as archived">
                                                <Chip
                                                    label="Deleted"
                                                    size="small"
                                                    sx={{
                                                        ml: 1,
                                                        height: 18,
                                                        fontSize: 10,
                                                        fontWeight: 700,
                                                        bgcolor: '#FEE2E2',
                                                        color: '#991B1B',
                                                        textTransform: 'uppercase',
                                                        letterSpacing: '.3px',
                                                    }}
                                                />
                                            </Tooltip>
                                        )}
                                    </FieldRow>
                                    <FieldRow label="Phone Number">{alert?.user?.phoneNumber || 'N/A'}</FieldRow>
                                    <FieldRow label="Backup Email">{alert?.user?.backupEmail || 'N/A'}</FieldRow>
                                    <FieldRow label="Blood Type">{alert?.user?.bloodType || 'N/A'}</FieldRow>
                                    <FieldRow label="Weight">{alert?.user?.weight || 'N/A'}</FieldRow>
                                    <FieldRow label="Height">{alert?.user?.height || 'N/A'}</FieldRow>

                                    {/* Resolution card — only rendered once the alert has been resolved.
                                        Auto-resolved alerts (e.g. non-app-initiated FakeCall) have no
                                        resolvedBy / resolvedComment; suppress those rows rather than
                                        rendering "N/A / —" noise. */}
                                    <When condition={isResolved}>
                                        <Grid item md={12} mt={1} sm={12} xs={12}>
                                            <Divider />
                                        </Grid>
                                        <SectionHeader>Resolution</SectionHeader>
                                        <FieldRow label="Resolved At">
                                            {alert?.resolvedAt ? dayjs(alert.resolvedAt).format('MM/DD/YYYY hh:mm A') : 'N/A'}
                                        </FieldRow>
                                        {alert?.resolvedBy && <FieldRow label="Resolved By">{alert.resolvedBy}</FieldRow>}
                                        {alert?.resolvedComment && <FieldRow label="Resolved Comment">{alert.resolvedComment}</FieldRow>}
                                    </When>
                                </Grid>

                                {showMiddleColumn && <Divider orientation="vertical" flexItem />}

                                {/* MIDDLE COLUMN — Timeline + (Call Recording | Manage Alert) */}
                                {showMiddleColumn && <Grid
                                    container
                                    spacing={2}
                                    p={2}
                                    md={4}
                                    sm={12}
                                    xs={12}
                                    alignContent={'flex-start'}
                                >
                                    <When condition={showAlertStatusTimeline}>
                                        <SectionHeader>Alert status timeline</SectionHeader>
                                        <Grid item md={12} sm={12} xs={12}>
                                            <Stepper orientation="vertical" activeStep={sortedDispatchTrail.length - 1 + (isSosFromCheckOnMe ? 1 : 0)}>
                                                {isSosFromCheckOnMe && (
                                                    <Step key="origin-check-on-me" completed expanded>
                                                        <StepLabel
                                                            sx={{
                                                                '& .MuiStepLabel-label': { fontWeight: 600, fontSize: 14, textTransform: 'none' },
                                                                '& .MuiStepIcon-root.Mui-completed': { color: 'warning.main' },
                                                            }}
                                                        >
                                                            Escalated from Check on Me
                                                        </StepLabel>
                                                        <StepContent>
                                                            <Stack spacing={0.5}>
                                                                <Typography fontSize={13}>
                                                                    The user didn't check out before their Check on Me timer expired — this SOS was opened automatically.
                                                                </Typography>
                                                                {alert?.initiatedAt && (
                                                                    <Typography fontSize={12} color="text.secondary">
                                                                        {dayjs(alert.initiatedAt).format('MM/DD/YYYY h:mm A')}
                                                                    </Typography>
                                                                )}
                                                                {alert?.checkOnMeId && onOpenAlert && (
                                                                    <Link
                                                                        component="button"
                                                                        variant="body2"
                                                                        underline="hover"
                                                                        sx={{ fontSize: 13, fontWeight: 500, alignSelf: 'flex-start' }}
                                                                        onClick={(e: React.MouseEvent) => {
                                                                            e.stopPropagation();
                                                                            onOpenAlert(alert.checkOnMeId!);
                                                                        }}
                                                                    >
                                                                        View original Check on Me →
                                                                    </Link>
                                                                )}
                                                            </Stack>
                                                        </StepContent>
                                                    </Step>
                                                )}
                                                {sortedDispatchTrail.map((entry, idx) => (
                                                    <Step
                                                        key={`dispatchTrail-${idx}-${entry.createdAt}`}
                                                        completed
                                                        active={idx === sortedDispatchTrail.length - 1}
                                                        expanded
                                                    >
                                                        <StepLabel
                                                            sx={{
                                                                '& .MuiStepLabel-label': {
                                                                    fontWeight: 600,
                                                                    fontSize: 14,
                                                                    textTransform: 'none',
                                                                },
                                                            }}
                                                        >
                                                            {entry.dispatchStatus || 'Status update'}
                                                        </StepLabel>
                                                        <StepContent>
                                                            <Stack spacing={0.5}>
                                                                <Typography fontSize={13}>
                                                                    {entry.message || '—'}
                                                                </Typography>
                                                                <Typography fontSize={12} color="text.secondary">
                                                                    {dayjs(entry.createdAt).format('MM/DD/YYYY h:mm A')}
                                                                </Typography>
                                                            </Stack>
                                                        </StepContent>
                                                    </Step>
                                                ))}
                                            </Stepper>
                                        </Grid>
                                    </When>

                                    {/* Call recording — SOS / Appointment only */}
                                    <When condition={showCallRecording}>
                                        <Grid item md={12} sm={12} xs={12}>
                                            <Divider flexItem sx={{ mb: 2 }} />
                                            <Typography variant="subtitle2" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.4, color: 'text.secondary', mb: 1.5 }}>
                                                Call Recording
                                            </Typography>
                                            <When condition={!isLoading && isFetched}>
                                                <Stack spacing={1.5}>
                                                    <Box>
                                                        <Typography fontWeight={500} variant="body2" sx={{ color: 'text.primary' }}>
                                                            Transcript
                                                        </Typography>
                                                        <Typography variant="body2" fontWeight={400} mt={0.5}>
                                                            {callTranscript?.TranscriptionText || "There's no transcript for that alert"}
                                                        </Typography>
                                                    </Box>
                                                    <Box>
                                                        <Typography fontWeight={500} variant="body2" sx={{ color: 'text.primary' }} mb={1}>
                                                            Audio
                                                        </Typography>
                                                        <If condition={callTranscript?.Media?.MediaFileUri}>
                                                            <Then>
                                                                <If condition={audioLoading}>
                                                                    <Then>
                                                                        <Typography variant="body2" fontWeight={400}>
                                                                            Loading audio…
                                                                        </Typography>
                                                                    </Then>
                                                                    <Else>
                                                                        <If condition={audioUrl}>
                                                                            <Then>
                                                                                <audio src={audioUrl} controls />
                                                                            </Then>
                                                                            <Else>
                                                                                <Typography variant="body2" fontWeight={400} color="error">
                                                                                    Failed to load audio
                                                                                </Typography>
                                                                            </Else>
                                                                        </If>
                                                                    </Else>
                                                                </If>
                                                            </Then>
                                                            <Else>
                                                                <Typography variant="body2" fontWeight={400}>
                                                                    There's no audio for that alert
                                                                </Typography>
                                                            </Else>
                                                        </If>
                                                    </Box>
                                                </Stack>
                                            </When>
                                            <When condition={!isFetched && isLoading}>
                                                <Loading loading={'Loading call recording transcript...'} />
                                            </When>
                                        </Grid>
                                    </When>

                                    {/* Risk Review survey — RiskReview alerts only */}
                                    <When condition={showRiskReview}>
                                        <Grid item md={12} sm={12} xs={12}>
                                            {(showAlertStatusTimeline || showCallRecording) && <Divider flexItem sx={{ mb: 2 }} />}
                                            <Typography variant="subtitle2" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.4, color: 'text.secondary', mb: 2 }}>
                                                Risk Review
                                            </Typography>
                                            {showRiskReviewScores && (
                                                <Stack direction="row" spacing={4} mb={alert?.riskReviewSurveyComment ? 3 : 0}>
                                                    {alert?.riskReviewSurveyHomeScore !== undefined && (
                                                        <Box>
                                                            <Typography fontSize={11} fontWeight={500} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.4 }}>
                                                                Home Score
                                                            </Typography>
                                                            <Typography fontSize={32} fontWeight={600} lineHeight={1.1} mt={0.5}>
                                                                {alert.riskReviewSurveyHomeScore}
                                                            </Typography>
                                                        </Box>
                                                    )}
                                                    {alert?.riskReviewSurveyNeighborhoodScore !== undefined && (
                                                        <Box>
                                                            <Typography fontSize={11} fontWeight={500} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.4 }}>
                                                                Neighborhood Score
                                                            </Typography>
                                                            <Typography fontSize={32} fontWeight={600} lineHeight={1.1} mt={0.5}>
                                                                {alert.riskReviewSurveyNeighborhoodScore}
                                                            </Typography>
                                                        </Box>
                                                    )}
                                                </Stack>
                                            )}
                                            {alert?.riskReviewSurveyComment && (
                                                <Box>
                                                    <Typography fontSize={11} fontWeight={500} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.4, mb: 1 }}>
                                                        Comment
                                                    </Typography>
                                                    <Box
                                                        sx={{
                                                            borderLeft: 3,
                                                            borderColor: 'primary.main',
                                                            backgroundColor: '#F4F6F8',
                                                            borderRadius: '0 4px 4px 0',
                                                            px: 2,
                                                            py: 1.5,
                                                        }}
                                                    >
                                                        <Typography fontSize={15} sx={{ whiteSpace: 'pre-wrap' }}>
                                                            {alert.riskReviewSurveyComment}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            )}
                                        </Grid>
                                    </When>

                                    {/* Lifecycle — Check on Me / Shift only */}
                                    <When condition={isCheckOnMeOrShift && lifecycleSteps.length > 0}>
                                        <Grid item md={12} sm={12} xs={12}>
                                            {showAlertStatusTimeline && <Divider flexItem sx={{ mb: 2 }} />}
                                            <Stack direction="row" spacing={2} alignItems="baseline" mb={1.5}>
                                                <Typography variant="subtitle2" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.4, color: 'text.secondary' }}>
                                                    {type === 'Shift' ? 'Shift lifecycle' : 'Check on Me lifecycle'}
                                                </Typography>
                                                <Typography fontSize={12} color="text.secondary">
                                                    Timer: {timerDisplay}
                                                </Typography>
                                            </Stack>
                                            <Stepper
                                                orientation="vertical"
                                                nonLinear
                                                activeStep={lifecycleSteps.findIndex((s) => s.state === 'current')}
                                            >
                                                {lifecycleSteps.map((step) => (
                                                    <Step
                                                        key={step.key}
                                                        completed={step.state === 'done'}
                                                        active={step.state === 'current'}
                                                        expanded
                                                    >
                                                        <StepLabel
                                                            sx={{
                                                                '& .MuiStepLabel-label': {
                                                                    fontWeight: step.state === 'done' || step.state === 'current' ? 600 : 400,
                                                                    color: step.state === 'pending' ? 'text.secondary' : 'text.primary',
                                                                },
                                                                ...(step.state === 'pending' && {
                                                                    '& .MuiStepIcon-root': {
                                                                        color: 'action.disabled',
                                                                    },
                                                                }),
                                                            }}
                                                        >
                                                            {step.label}
                                                        </StepLabel>
                                                        <StepContent>
                                                            {typeof step.caption === 'string' ? (
                                                                <Typography fontSize={13} color="text.secondary">
                                                                    {step.caption}
                                                                </Typography>
                                                            ) : (
                                                                step.caption
                                                            )}
                                                        </StepContent>
                                                    </Step>
                                                ))}
                                            </Stepper>
                                        </Grid>
                                    </When>
                                </Grid>}

                                <Divider orientation="vertical" flexItem />

                                {/* RIGHT COLUMN — Location */}
                                <Grid
                                    container
                                    spacing={2}
                                    p={2}
                                    md={sideColumnSize}
                                    sm={12}
                                    xs={12}
                                    alignContent={'flex-start'}
                                >
                                    <When condition={location?.latitude && location.longitude}>
                                        <AlertPathMapComponent
                                            showAllLocations={showAllLocations}
                                            setShowAllLocations={setShowAllLocations}
                                            location={location}
                                            alert={alert}
                                        />
                                    </When>
                                    <Grid item md={12} sm={12} xs={12}>
                                        <Typography fontSize={13} fontWeight={500} color="text.secondary" mb={0.5}>
                                            Address
                                        </Typography>
                                        {(() => {
                                            if (resolvedAddress) {
                                                return (
                                                    <Typography fontSize={14} fontWeight={400}>
                                                        {resolvedAddress}
                                                    </Typography>
                                                );
                                            }
                                            if (!hasCoords) {
                                                return (
                                                    <Typography fontSize={14} fontWeight={400}>
                                                        N/A
                                                    </Typography>
                                                );
                                            }
                                            if (resolveError) {
                                                return (
                                                    <Typography fontSize={14} fontWeight={400} color="error">
                                                        {resolveError}
                                                    </Typography>
                                                );
                                            }
                                            // Has coords but no result yet — covers both the pre-effect
                                            // first render and the in-flight lookup, so no N/A flash.
                                            return (
                                                <Typography fontSize={14} fontWeight={400} color="text.secondary">
                                                    Looking up address…
                                                </Typography>
                                            );
                                        })()}
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Box>
                    </Box>
                </Stack>
            </Drawer>

            {resolveModal &&
                <ResolveAlertModal
                    visible={resolveModal}
                    type={type!}
                    onClose={async () => {
                        await onClose();
                        setResolveModal(false);
                        if (zoomOut) {
                            zoomOut();
                        }
                    }}
                    alert={alert}
                    onResolve={() => {
                        setEscalated(true);
                    }}
                />
            }
        </>
    );
};
